import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '../components/Button';
import { ShieldIcon, TargetIcon, AwardIcon, GlobeIcon, LockIcon, ClipboardIcon, BrainIcon, DocumentIcon } from '../components/Icons';
import { getPageMeta } from '../utils/seoHelmet';

const About = () => {
    const [isIntroVisible, setIsIntroVisible] = useState(false);
    const [isAwardsVisible, setIsAwardsVisible] = useState(false);
    const [isMetricsVisible, setIsMetricsVisible] = useState(false);
    const [isTimelineVisible, setIsTimelineVisible] = useState(false);
    const [isMissionVisible, setIsMissionVisible] = useState(false);
    const [isValuesVisible, setIsValuesVisible] = useState(false);
    const [isTestimonialsVisible, setIsTestimonialsVisible] = useState(false);
    const [isFaqVisible, setIsFaqVisible] = useState(false);
    const introRef = useRef(null);
    const awardsRef = useRef(null);
    const metricsRef = useRef(null);
    const timelineRef = useRef(null);
    const missionRef = useRef(null);
    const valuesRef = useRef(null);
    const testimonialsRef = useRef(null);
    const faqRef = useRef(null);

    // Scroll animations
    useEffect(() => {
        const observers = [];
        
        const createObserver = (ref, setter) => {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        setter(entry.isIntersecting);
                    });
                },
                { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
            );
            if (ref.current) observer.observe(ref.current);
            return observer;
        };

        observers.push(createObserver(introRef, setIsIntroVisible));
        observers.push(createObserver(awardsRef, setIsAwardsVisible));
        observers.push(createObserver(metricsRef, setIsMetricsVisible));
        observers.push(createObserver(timelineRef, setIsTimelineVisible));
        observers.push(createObserver(missionRef, setIsMissionVisible));
        observers.push(createObserver(valuesRef, setIsValuesVisible));
        observers.push(createObserver(testimonialsRef, setIsTestimonialsVisible));
        observers.push(createObserver(faqRef, setIsFaqVisible));

        return () => observers.forEach(obs => obs.disconnect());
    }, []);

    const values = [
        {
            icon: <ShieldIcon size={32} />,
            title: 'Integrity',
            description: 'We act with transparency and honesty in every engagement—building trust through ethical practices, clear communication, and responsible decision-making.'
        },
        {
            icon: <AwardIcon size={32} />,
            title: 'Accountability',
            description: 'We take ownership of our work and its outcomes, ensuring measurable results, documented processes, and clear accountability at every stage.'
        },
        {
            icon: <TargetIcon size={32} />,
            title: 'Excellence',
            description: 'We are committed to delivering enterprise-grade quality—maintaining high standards in methodology, execution, and professional conduct.'
        },
        {
            icon: <GlobeIcon size={32} />,
            title: 'Responsible Innovation',
            description: 'We embrace innovation with discipline—leveraging AI and emerging technologies responsibly, balancing progress with governance and security.'
        }
    ];

    const timeline = [
        {
            label: 'Compliance-first mindset'
        },
        {
            label: 'Practical, business-aligned delivery'
        },
        {
            label: 'Security by design'
        },
        {
            label: 'Long-term partnership approach'
        },
        {
            label: 'Continuous improvement culture'
        }
    ];

    const awards = [
        {
            icon: <ShieldIcon size={32} />,
            label: 'ISO 9001 Quality Management principles',
            description: 'Comprehensive quality management framework ensuring consistent, high-quality delivery across all our services.'
        },
        {
            icon: <LockIcon size={32} />,
            label: 'ISO/IEC 27001 Information Security best practices',
            description: 'Enterprise-grade information security standards protecting sensitive data and ensuring compliance.'
        },
        {
            icon: <TargetIcon size={32} />,
            label: 'GDPR-ready data protection approach',
            description: 'Privacy-first data handling practices that align with global data protection regulations.'
        },
        {
            icon: <AwardIcon size={32} />,
            label: 'Enterprise-grade governance and delivery standards',
            description: 'Professional methodologies and frameworks designed for regulated and enterprise environments.'
        }
    ];

    const metrics = [
        {
            icon: <GlobeIcon size={32} />,
            label: 'Enterprise-focused delivery model',
            description: 'Scalable solutions designed for complex organizational needs and global operations.'
        },
        {
            icon: <ClipboardIcon size={32} />,
            label: 'ISO-aligned consulting frameworks',
            description: 'Methodologies grounded in international standards for quality and information security.'
        },
        {
            icon: <BrainIcon size={32} />,
            label: 'Practical AI enablement approach',
            description: 'Focusing on real-world application and workforce upskilling rather than hype.'
        },
        {
            icon: <LockIcon size={32} />,
            label: 'Security-first system design',
            description: 'Embedding security at every layer of implementation to protect your critical assets.'
        },
        {
            icon: <DocumentIcon size={32} />,
            label: 'Audit-ready documentation & governance',
            description: 'Comprehensive documentation ensuring full traceability and regulatory compliance.'
        }
    ];

    const [expandedFaq, setExpandedFaq] = useState(null);

    const faqs = [
        {
            question: 'What does Phoenix AI specialize in?',
            answer: 'Phoenix AI provides enterprise-focused solutions in AI enablement, ISO consultancy (ISO 9001 & ISO/IEC 27001), and AI-powered security platforms—helping organizations adopt technology responsibly, securely, and in alignment with international standards.'
        },
        {
            question: 'Do you work with SMEs or only large enterprises?',
            answer: 'We work with both SMEs and enterprise organizations. Our approach is scalable—practical for growing businesses and robust enough for regulated and enterprise environments.'
        },
        {
            question: 'Are your ISO consultancy services certification bodies?',
            answer: 'No. Phoenix AI is a consultancy and implementation partner. We support organizations in preparing for certification by developing systems, documentation, and audit readiness, but certification is conducted by independent accredited certification bodies.'
        },
        {
            question: 'Can you help organizations that are new to ISO standards?',
            answer: 'Yes. We frequently work with organizations starting their ISO journey, guiding them step by step—from understanding requirements to building practical, audit-ready management systems.'
        },
        {
            question: 'Is your AI training suitable for non-technical teams?',
            answer: 'Absolutely. Our AI enablement programs are designed for business professionals, managers, and leaders—focusing on practical application, responsible use, and real workplace impact, not technical coding.'
        },
        {
            question: 'What makes Phoenix AI different from other providers?',
            answer: 'We combine AI enablement, governance frameworks, and enterprise-grade security into a single delivery model—focusing on accountability, documentation, and real-world execution rather than generic technology deployment.'
        },
        {
            question: 'How do we start working with Phoenix AI?',
            answer: 'You can begin by contacting us through our website or WhatsApp. We will schedule an initial consultation to understand your objectives and recommend the most suitable engagement approach.'
        }
    ];

    const meta = getPageMeta('about');

    return (
        <main>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <meta name="keywords" content={meta.keywords} />
                <meta name="robots" content={meta.robots} />
                <link rel="canonical" href={meta.canonical} />
                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:image" content={meta.ogImage} />
                <meta property="og:url" content={meta.canonical} />
            </Helmet>
            {/* Page Hero */}
            <section 
                className="page-hero page-hero--dark" 
                style={{ 
                    backgroundImage: `linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(15, 15, 35, 0.95)), url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=600&fit=crop&q=80')` 
                }}
            >
                <div className="container">
                    <span className="page-hero__eyebrow">About Us</span>
                    <h1 className="page-hero__title">About Phoenix AI</h1>
                    <p className="page-hero__subtitle">
                         Phoenix AI delivers enterprise-grade AI, compliance, and security solutions for organizations that value trust and measurable results.
                    </p>
                </div>
            </section>

            {/* About Introduction */}
            <section 
                className={`section about-intro ${isIntroVisible ? 'about-intro--visible' : ''}`}
                ref={introRef}
            >
                <div className="container about-intro__container">
                    <div className="about-intro__image">
                        <img 
                            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=450&fit=crop" 
                            alt="Professional corporate office environment" 
                        />
                        <div className="about-intro__image-accent"></div>
                    </div>
                    <div className="about-intro__content">
                        <span className="about-intro__eyebrow">Our Story</span>
                        <h2>Built for the Future. Trusted for Today.</h2>
                        <p>
                            Phoenix AI was established to help organizations navigate the intersection of intelligence, 
                            compliance, and security in an increasingly complex business landscape.
                        </p>
                        <p>
                            As AI and digital technologies reshape how enterprises operate, we believe success depends not 
                            only on innovation—but on how responsibly, securely, and sustainably that innovation is implemented.
                        </p>
                        <p>
                            Our approach combines practical AI enablement, internationally aligned frameworks, and enterprise-grade 
                            security solutions to support organizations at every stage of their transformation journey.
                        </p>
                        <p>
                            With deep experience across technology, governance, and risk management, Phoenix AI partners with 
                            organizations that value accountability, transparency, and long-term resilience.
                        </p>
                        <Button to="/contact">
                            Start a Conversation
                        </Button>
                    </div>
                </div>
            </section>

            {/* Company Metrics Section */}
            <section 
                className={`section metrics-section ${isMetricsVisible ? 'metrics-section--visible' : ''}`}
                ref={metricsRef}
            >
                <div className="container">
                    <div className="metrics-section__header">
                        <span className="metrics-section__eyebrow">Core Pillars</span>
                        <h2 className="metrics-section__title">What Defines Phoenix AI</h2>
                    </div>
                    <div className="metrics-grid">
                        {metrics.map((metric, index) => (
                            <div key={index} className="metric-card">
                                <div className="metric-card__icon">
                                    {metric.icon}
                                </div>
                                <h3 className="metric-card__title">{metric.label}</h3>
                                <p className="metric-card__description">{metric.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Awards & Certifications Section */}
            <section 
                className={`section awards-section ${isAwardsVisible ? 'awards-section--visible' : ''}`}
                ref={awardsRef}
            >
                <div className="container">
                    <div className="standards-container">
                        <div className="standards-header">
                            <span className="standards-header__eyebrow">Our Standards</span>
                            <h2 className="standards-header__title">Our Standards & Commitments</h2>
                            <p className="standards-header__subtitle">Built on Global Best Practices</p>
                            <p className="standards-header__description">
                                Phoenix AI aligns its solutions with internationally recognized standards and enterprise best practices 
                                to ensure security, quality, and trust—without making claims we cannot yet certify.
                            </p>
                            <p className="standards-header__label">We align with:</p>
                        </div>
                        <div className="standards-grid">
                            {awards.map((award, index) => (
                                <div key={index} className="standard-card">
                                    <div className="standard-card__icon">
                                        {award.icon}
                                    </div>
                                    <h3 className="standard-card__title">{award.label}</h3>
                                    <p className="standard-card__description">{award.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Timeline Section */}
            <section 
                className={`section timeline-section ${isTimelineVisible ? 'timeline-section--visible' : ''}`}
                ref={timelineRef}
            >
                <div className="container">
                    <div className="journey-container">
                        <div className="journey-header">
                            <span className="journey-header__eyebrow">Our Journey</span>
                            <h2 className="journey-header__title">The Beginning of Phoenix AI</h2>
                            <p className="journey-header__description">
                                2026 marks the start of Phoenix AI's journey—founded with a clear vision to help organizations adopt 
                                intelligence responsibly through structured AI enablement, international standards, and enterprise-grade security.
                            </p>
                            <p className="journey-header__tagline">
                                Our focus from day one is simple: build trust, deliver value, and grow through long-term partnerships.
                            </p>
                            <p className="journey-header__label">What Guides Our Journey</p>
                        </div>
                        <div className="journey-guides">
                            {timeline.map((item, index) => (
                                <div key={index} className="journey-item">
                                    <div className="journey-item__number">{index + 1}</div>
                                    <p className="journey-item__label">{item.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision Section */}
            <section 
                className={`section mission-vision-section ${isMissionVisible ? 'mission-vision-section--visible' : ''}`}
                ref={missionRef}
            >
                <div className="container">
                    <div className="mission-vision-grid">
                        <div className="mission-vision-item">
                            <span className="mission-vision-item__label">Our Mission</span>
                            <h3 className="mission-vision-item__title">Enabling Intelligent, Trusted Organizations</h3>
                            <p className="mission-vision-item__text">
                                To help organizations adopt AI and digital solutions responsibly—through structured enablement, international standards, and enterprise-grade security—so innovation can scale with confidence, control, and accountability.
                            </p>
                        </div>
                        <div className="mission-vision-item">
                            <span className="mission-vision-item__label">Our Vision</span>
                            <h3 className="mission-vision-item__title">Shaping the Future of Responsible Intelligence</h3>
                            <p className="mission-vision-item__text">
                                To become a trusted partner in building intelligent, compliant, and secure enterprises—where technology advances hand in hand with governance, resilience, and long-term trust.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section 
                className={`section values-section ${isValuesVisible ? 'values-section--visible' : ''}`}
                ref={valuesRef}
            >
                <div className="container">
                    <div className="values-section__header">
                        <span className="values-section__eyebrow">What Drives Phoenix AI</span>
                        <h2 className="values-section__title">Our Core Values</h2>
                        <p className="values-section__subtitle">The principles that guide how we build, deliver, and partner with our clients.</p>
                    </div>
                    <div className="values-grid">
                        {values.map((value, index) => (
                            <div key={index} className="value-item">
                                <div className="value-item__icon">
                                    {value.icon}
                                </div>
                                <h3 className="value-item__title">{value.title}</h3>
                                <p className="value-item__text">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Client Testimonials Section */}
            <section 
                className={`section testimonials-section ${isTestimonialsVisible ? 'testimonials-section--visible' : ''}`}
                ref={testimonialsRef}
            >
                <div className="container">
                    <div className="testimonials-section__header">
                        <span className="testimonials-section__eyebrow">Our Commitment</span>
                        <h2 className="testimonials-section__title">What Clients Can Expect</h2>
                        <p className="testimonials-section__subtitle">Our Commitment to Every Engagement</p>
                    </div>
                    <div className="expectations-content">
                        <p className="expectations-intro">
                            At Phoenix AI, every client engagement is guided by the same principles—regardless of project size or industry.
                        </p>
                        <ul className="expectations-list">
                            <li className="expectations-item">Clear scope, documented deliverables</li>
                            <li className="expectations-item">Compliance-first and security-aware approach</li>
                            <li className="expectations-item">Structured implementation methodology</li>
                            <li className="expectations-item">Transparent communication and accountability</li>
                            <li className="expectations-item">Long-term partnership mindset</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section 
                className={`section faq-section ${isFaqVisible ? 'faq-section--visible' : ''}`}
                ref={faqRef}
            >
                <div className="container">
                    <div className="faq-section__header">
                        <span className="faq-section__eyebrow">Common Questions</span>
                        <h2 className="faq-section__title">Frequently Asked Questions</h2>
                        <p className="faq-section__subtitle">Get answers to common questions about our services, compliance, and implementation.</p>
                    </div>
                    <div className="faq-container">
                        {faqs.map((faq, index) => (
                            <div key={index} className="faq-item">
                                <button
                                    className="faq-item__header"
                                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                                >
                                    <h3 className="faq-item__question">{faq.question}</h3>
                                    <span className={`faq-item__icon ${expandedFaq === index ? 'faq-item__icon--open' : ''}`}>+</span>
                                </button>
                                {expandedFaq === index && (
                                    <div className="faq-item__content">
                                        <p className="faq-item__answer">{faq.answer}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default About;
